/**
 * Searchable Multi-Select — eine Combobox mit Such-Input und Mehrfach-Auswahl.
 *
 * Wird in der Mitarbeiter-Filterleiste fuer Felder mit potenziell vielen
 * Optionen verwendet (Massnahmen, Abteilungen, Position, Wohnort, ...).
 *
 * Props:
 *   options:  Array<{ value, label, hint? }>
 *   selected: string[]
 *   onChange: (next: string[]) => void
 *   placeholder, emptyText, triggerTestId, label
 */
import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import { cn } from '../../lib/utils';

export const SearchableMultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Auswählen...',
  emptyText = 'Nichts gefunden.',
  searchPlaceholder = 'Suchen...',
  triggerTestId,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const labelOf = (v) => options.find((o) => o.value === v)?.label || v;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal h-10', className)}
          data-testid={triggerTestId}
        >
          <span className="flex items-center gap-1 flex-1 min-w-0 truncate text-left">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : selected.length <= 2 ? (
              selected.map((v) => (
                <Badge key={v} variant="secondary" className="text-xs">
                  {labelOf(v)}
                </Badge>
              ))
            ) : (
              <Badge variant="secondary" className="text-xs">
                {selected.length} ausgewählt
              </Badge>
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {selected.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={clear}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && clear(e)}
                className="hover:bg-muted rounded p-0.5 -m-0.5"
                title="Zurücksetzen"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label + ' ' + (opt.hint || '')}
                    onSelect={() => toggle(opt.value)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint && (
                      <span className="ml-2 text-xs text-muted-foreground">{opt.hint}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableMultiSelect;
